import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Inbox, Calendar, CheckCircle2, Settings, Sun, LogOut, Plus, Compass } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import AddSomethingModal from "./AddSomethingModal";

export default function Layout({ children, onDataChanged }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);

  const desktopNav = [
    { name: t("nav.dashboard"), path: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { name: t("nav.inbox"), path: "/inbox", icon: Inbox, testid: "nav-inbox" },
    { name: t("nav.upcoming"), path: "/upcoming", icon: Calendar, testid: "nav-upcoming" },
    { name: t("nav.completed"), path: "/completed", icon: CheckCircle2, testid: "nav-completed" },
    { name: t("nav.settings"), path: "/settings", icon: Settings, testid: "nav-settings" },
  ];
  const mobileNav = [
    { name: t("nav.today"), path: "/dashboard", icon: Sun, testid: "mobile-nav-today" },
    { name: t("nav.inbox"), path: "/inbox", icon: Inbox, testid: "mobile-nav-inbox" },
    { name: t("nav.upcoming"), path: "/upcoming", icon: Calendar, testid: "mobile-nav-upcoming" },
    { name: t("nav.settings"), path: "/settings", icon: Settings, testid: "mobile-nav-settings" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden md:flex md:flex-col w-64 border-r border-border bg-card/60 backdrop-blur-sm fixed h-screen">
        <div className="px-6 py-6 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg brand-bg flex items-center justify-center">
            <Compass className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">{t("common.appName")}</span>
        </div>

        <div className="px-4">
          <Button data-testid="btn-add-something" onClick={() => setAddOpen(true)}
            className="w-full rounded-full brand-bg text-white hover:opacity-90 font-medium">
            <Plus className="h-4 w-4 mr-1" /> {t("common.addSomething")}
          </Button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {desktopNav.map((item) => (
            <NavLink key={item.path} to={item.path} data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
              <item.icon className="h-4 w-4" />
              {item.name}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <button data-testid="btn-logout" onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 pb-20 md:pb-0">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">{children}</div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-card border-t border-border flex items-center justify-around z-40">
        {mobileNav.map((item) => (
          <NavLink key={item.testid} to={item.path} data-testid={item.testid}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 text-xs ${isActive ? "brand-text" : "text-muted-foreground"}`}>
            <item.icon className="h-5 w-5" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      <button data-testid="btn-add-something-mobile" onClick={() => setAddOpen(true)}
        className="md:hidden fixed bottom-20 right-5 h-14 w-14 rounded-full brand-bg text-white shadow-lg flex items-center justify-center z-40">
        <Plus className="h-6 w-6" />
      </button>

      <AddSomethingModal open={addOpen} onOpenChange={setAddOpen} onSaved={onDataChanged} />
    </div>
  );
}
